import {
  useLayoutEffect,
  useRef,
  type ChangeEvent,
  type ClipboardEvent,
  type KeyboardEvent,
} from 'react'
import {
  SNILS_PLACEHOLDER,
  countDigitsBefore,
  formatSnils,
  keepSnilsDigits,
  snilsCaretPosition,
} from '@/lib/snils'
import { Input } from './input'

/**
 * SnilsInput — поле СНИЛС с автоматической маской (centrika-docs!46, `common.md` →
 * «Поля СНИЛС»). Применяется ко ВСЕМ полям ввода и изменения СНИЛС.
 *
 * При наборе и вставке из значения удаляются все символы кроме цифр, ввод ограничен
 * 11 цифрами, маска `000-000-000 00` формируется по мере ввода. Наружу компонент отдаёт
 * значение В МАСКЕ — в том же виде, в каком его видит пользователь; проверка корректности
 * (`isValidSnils`) разделители не учитывает, а серверная проверка дублей сравнивает СНИЛС
 * по цифрам, поэтому формат хранения на них не влияет.
 *
 * В отличие от `DateInput` собственного текстового состояния нет: маска однозначна на
 * любом префиксе цифр, поэтому промежуточных «незавершённых» значений, которые нельзя
 * отдать наружу, не бывает.
 */
export interface SnilsInputProps {
  /** Значение поля; принимается в любом виде (с маской или без) и показывается в маске. */
  value: string
  /** Вызывается со значением В МАСКЕ (`000-000-000 00`). */
  onChange: (value: string) => void
  /** Уход с поля — например, чтобы сохранить введённое значение. */
  onBlur?: () => void
  id?: string
  invalid?: boolean
  disabled?: boolean
  className?: string
  placeholder?: string
  'aria-invalid'?: boolean
  'aria-describedby'?: string
  'aria-label'?: string
  /** Обязательность для вспомогательных технологий (`Field` отдаёт её в render-аргументах). */
  'aria-required'?: true | undefined
}

export function SnilsInput({
  value,
  onChange,
  onBlur,
  id,
  invalid,
  disabled,
  className,
  placeholder = SNILS_PLACEHOLDER,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  'aria-label': ariaLabel,
  'aria-required': ariaRequired,
}: SnilsInputProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  /** Позиция каретки после форматирования: сама маска не должна двигать курсор. */
  const caret = useRef<number | null>(null)

  useLayoutEffect(() => {
    if (caret.current === null || !inputRef.current) return
    inputRef.current.setSelectionRange(caret.current, caret.current)
    caret.current = null
  })

  function apply(raw: string, caretPos: number) {
    const digitsBefore = countDigitsBefore(raw, caretPos)
    const formatted = formatSnils(raw)
    caret.current = snilsCaretPosition(formatted, digitsBefore)
    onChange(formatted)
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    apply(e.target.value, e.target.selectionStart ?? e.target.value.length)
  }

  /**
   * Backspace/Delete на РАЗДЕЛИТЕЛЕ маски. Разделители выводятся из числа цифр, поэтому
   * их удаление само по себе ничего не меняет — значение пересобиралось бы тем же, и
   * пользователю приходилось бы жать Backspace дважды при правке середины значения.
   * Удаляем ближайшую цифру за разделителем — как в привычных полях с маской.
   */
  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    const el = e.currentTarget
    const start = el.selectionStart ?? 0
    const end = el.selectionEnd ?? start
    // Выделение удаляется как обычно — там цифры точно затрагиваются.
    if (start !== end) return
    const isDigit = (i: number) => /\d/.test(el.value[i] ?? '')

    if (e.key === 'Backspace' && start > 0 && !isDigit(start - 1)) {
      let i = start - 1
      while (i >= 0 && !isDigit(i)) i -= 1
      if (i < 0) return
      e.preventDefault()
      apply(el.value.slice(0, i) + el.value.slice(i + 1), i)
      return
    }
    if (e.key === 'Delete' && start < el.value.length && !isDigit(start)) {
      let i = start
      while (i < el.value.length && !isDigit(i)) i += 1
      if (i >= el.value.length) return
      e.preventDefault()
      apply(el.value.slice(0, i) + el.value.slice(i + 1), i)
    }
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData('text')
    if (!pasted) return
    e.preventDefault()
    const el = e.currentTarget
    const start = el.selectionStart ?? el.value.length
    const end = el.selectionEnd ?? start
    // Вставка тоже чистится от нецифровых символов и обрезается по длине.
    const merged = el.value.slice(0, start) + keepSnilsDigits(pasted) + el.value.slice(end)
    apply(merged, start + keepSnilsDigits(pasted).length)
  }

  return (
    <Input
      ref={inputRef}
      id={id}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      placeholder={placeholder}
      value={formatSnils(value)}
      invalid={invalid}
      disabled={disabled}
      onBlur={onBlur}
      className={className}
      aria-invalid={ariaInvalid ?? invalid}
      aria-describedby={ariaDescribedBy}
      aria-label={ariaLabel}
      aria-required={ariaRequired}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
    />
  )
}
